import React, { useContext, useEffect, useState } from 'react';
import { DraggableItemData } from '../../pages/Common/DraggableItem';
import { TableColumn } from '../../../types/table_settings/TableColumn';
import { TableSettingsContext } from '../../../context/TableSettingsContext';
import { TableSettings } from '../../../types/table_settings/TableSettings';
import { CustomField, CustomFieldEntityType } from '../../../types/custom_field';
import { CustomFieldContext } from '../../../context/CustomFieldContext';
import { useRequest } from '../../../requests/common/requestHook';
import { TableSettingsRequest } from '../../../requests/tableSettings.request';
import { TableSettingsInput } from '../../../types/table_settings/tableSettings.input';
import { ToastType } from '../../components/Toast';
import { translate } from '../../../types/translation/Translator';
import { TranslationKey } from '../../../types/translation/TranslationKey';
import { useToast } from '../../../providers/ToastProvider';
import { DraggableList } from '../../pages/Common/DraggableList';
import Icon, { IconType } from '../../icons/Icon';
import { TextInput } from '../../components/TextInput';
import { DeleteIconButton } from '../../icons';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { DropdownSearchSelect } from '../../components/Dropdown';
import { SaveButton } from '../../components/Button';

const TABLE_SETTINGS_MAX_COLUMNS = 12;

interface DropdownItem<T> {
  label: string;
  column: T | string;
}

interface DraggableColumnField extends DraggableItemData, TableColumn<any> {}

export interface TableSettingsContentProps<T> {
  settings: TableSettings<any>;
  customFieldEntityType?: CustomFieldEntityType;
  availableColumns?: T[];
  columnTextBuilder: (column: T) => string;
  label: string;
  placeholder: string;
}

export default function TableSettingsContent<T>({
  settings,
  customFieldEntityType,
  availableColumns,
  columnTextBuilder,
  label,
  placeholder,
}: TableSettingsContentProps<T>) {
  const { showToast } = useToast();

  const { tableSettings, setTableSettings } = useContext(TableSettingsContext);
  const { customFields } = useContext(CustomFieldContext);

  const inputToItems = () => settings.columns.map((col, id) => ({ ...col, id, key: `${col.index}${col.label}` }));

  const [currentInput, setCurrentInput] = useState(() => {
    return new TableSettingsInput({
      ...settings,
      columns: settings.columns.map((i, idx) => ({ column: i.column, label: i.label, index: idx })),
    });
  });

  const [items, setItems] = useState<DraggableColumnField[]>(() => inputToItems());

  useEffect(() => {
    setItems(inputToItems());
  }, [settings]);

  const customFieldsForEntity = Array.from(customFields.values()).filter(
    (i: CustomField) => i.entityType === customFieldEntityType,
  );

  const dropdownItems: DropdownItem<T>[] = [
    ...(availableColumns || [])
      .filter(col => items.findIndex(item => item.column === col) === -1)
      .map(col => ({ label: columnTextBuilder(col), column: col })),
    ...(customFieldsForEntity ?? [])
      .filter(field => items.findIndex(item => item.column === field.name) === -1)
      .map(field => ({ label: field.name, column: field.name })),
  ];

  const handleDelete = (id: number) => {
    const index = items.findIndex(item => item.id === id);
    if (index === -1) return;
    items.splice(index, 1);
    setItems([...items]);
  };

  const handleAdd = (item: DraggableColumnField) => {
    setItems([...items, item]);
  };

  const [create, { loading: createLoading }] = useRequest(TableSettingsRequest.create);
  const [update, { loading: updateLoading }] = useRequest(TableSettingsRequest.update);

  const handleSave = async () => {
    let updatedTableSettings;
    if (currentInput.version === 0) {
      updatedTableSettings = await create({ data: [currentInput.withColumns(items).forCreate()] });
    } else {
      updatedTableSettings = await update({ data: [currentInput.withColumns(items).forUpdate()] });
    }

    if (!updatedTableSettings || !updatedTableSettings.length) return;

    setCurrentInput(new TableSettingsInput(updatedTableSettings[0]));

    showToast({
      type: ToastType.success,
      title: translate({ key: TranslationKey.tableSettingsUpdatedSuccessfully }),
    });

    const next = new Map(tableSettings);
    updatedTableSettings.forEach(settings => {
      next.set(settings.id, settings);
    });
    setTableSettings(next);
  };

  const canSave =
    items.length > 0 && items.length <= 12 && !createLoading && !updateLoading && currentInput.hasChanges(items);

  const draggableItem = (item: DraggableColumnField) => {
    return (
      <div className='grid w-full grid-cols-5 items-center gap-4'>
        <div className='col-span-2 flex items-center gap-2'>
          <Icon type={IconType.switchVertical1} size={'h-6 w-6'} stroke={'stroke-gray-900'} />
          <div className='flex-grow items-start text-sm'>{columnTextBuilder(item.column)}</div>
          <Icon type={IconType.arrowBlockRight} size={'h-6 w-6'} stroke={'stroke-gray-900'} />
        </div>

        <div className='col-span-3 flex items-center gap-2'>
          <div className='flex-grow'>
            <TextInput
              className='w-full'
              disabled={createLoading || updateLoading}
              value={item.label}
              placeholder={columnTextBuilder(item.column)}
              onChange={value => {
                item.label = value;
                setItems([...items]);
              }}
            />
          </div>
          <DeleteIconButton onClick={() => handleDelete(item.id)} />
        </div>
      </div>
    );
  };

  return (
    <div className={`flex h-full flex-col gap-4 overflow-auto`}>
      <DropdownSearchSelect<DropdownItem<T> | undefined>
        label={label}
        placeholder={placeholder}
        items={dropdownItems}
        disabled={items.length >= TABLE_SETTINGS_MAX_COLUMNS || !dropdownItems.length || createLoading || updateLoading}
        value={undefined}
        textBuilder={item => item?.label || ''}
        itemBuilder={item => item?.label || ''}
        onSelect={item => {
          if (item) handleAdd({ ...item, index: items.length, id: items.length, key: `${item.column}${items.length}` });
        }}
      />
      <div className='flex-grow overflow-auto'>
        <DndProvider backend={HTML5Backend}>
          <DraggableList displayFn={draggableItem} items={items} setItems={setItems} />
        </DndProvider>
      </div>
      <div className='flex justify-end gap-2'>
        <SaveButton onClick={handleSave} disabled={!canSave} loading={createLoading || updateLoading} />
      </div>
    </div>
  );
}
